import React, { useState } from 'react';
import { bool, func, string, object } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { Button, IconCheckmark, IconClose } from '../../../components';
import { applyDiscountCodeApi, validateDiscountCodeApi } from '../../../util/api';

import css from './CouponCodeField.module.css';

const CouponCodeField = props => {
  const {
    className,
    rootClassName,
    name = 'couponCode',
    onCouponApplied,
    onCouponRemoved,
    appliedCoupon,
    listingId,
    orderData,
    disabled = false,
    sessionTypeId,
  } = props;

  const intl = useIntl();
  const [couponCode, setCouponCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  const classes = classNames(rootClassName || css.root, className);

  const computeOrderTotal = () => {
    if (orderData?.total?.amount) {
      return parseInt(orderData.total.amount, 10);
    }
    if (orderData?.lineItems) {
      return orderData.lineItems.reduce((total, item) => total + (item.lineTotal?.amount || 0), 0);
    }
    return 0;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setValidationError(intl.formatMessage({ id: 'CouponCodeField.emptyCode' }));
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      const normalizedCode = couponCode.trim().toUpperCase();
      const orderTotal = computeOrderTotal();
      const currency = orderData?.total?.currency || 'USD';

      await validateDiscountCodeApi({
        couponCode: normalizedCode,
        listingId,
        orderTotal,
        currency,
        sessionTypeId,
      });

      const applyResponse = await applyDiscountCodeApi({
        couponCode: normalizedCode,
        listingId,
        orderTotal,
        currency,
        sessionTypeId,
      });

      const coupon = applyResponse?.data?.coupon || applyResponse?.coupon;
      if (coupon) {
        onCouponApplied({
          id: coupon.id,
          code: coupon.code,
          discount: coupon.amount,
          type: coupon.type,
          currency: coupon.currency || currency,
        });
        setCouponCode('');
      } else {
        setValidationError(
          applyResponse?.error || intl.formatMessage({ id: 'CouponCodeField.applyError' })
        );
      }
    } catch (error) {
      if (error?.error) {
        setValidationError(error.error);
      } else if (error?.status === 404) {
        setValidationError(intl.formatMessage({ id: 'CouponCodeField.invalidCode' }));
      } else {
        setValidationError(
          error?.message || intl.formatMessage({ id: 'CouponCodeField.networkError' })
        );
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    onCouponRemoved();
    setValidationError(null);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApplyCoupon();
    }
  };

  if (appliedCoupon) {
    return (
      <div className={classes}>
        <div className={css.appliedCoupon}>
          <div className={css.appliedCouponInfo}>
            <IconCheckmark className={css.successIcon} />
            <span className={css.appliedCouponCode}>
              <FormattedMessage id="CouponCodeField.appliedCoupon" values={{ code: appliedCoupon.code }} />
            </span>
            <span className={css.discountAmount}>
              {appliedCoupon.type === 'percentage'
                ? `${appliedCoupon.discount}%`
                : `$${Number(appliedCoupon.discount).toFixed(2)}`}
            </span>
          </div>
          <Button
            type="button"
            onClick={handleRemoveCoupon}
            className={css.removeButton}
            disabled={disabled}
          >
            <IconClose className={css.removeIcon} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes}>
      <div className={css.couponContainer}>
        <div className={css.couponLabel}>
          <FormattedMessage id="CouponCodeField.label" />
        </div>
        <div className={css.couponInputContainer}>
          <input
            id={name}
            name={name}
            type="text"
            value={couponCode}
            onChange={e => setCouponCode(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={intl.formatMessage({ id: 'CouponCodeField.placeholder' })}
            className={css.couponInput}
            disabled={disabled || isValidating}
            autoComplete="off"
          />
          <Button
            type="button"
            onClick={handleApplyCoupon}
            className={css.applyButton}
            disabled={disabled || isValidating || !couponCode.trim()}
            inProgress={isValidating}
          >
            <FormattedMessage id="CouponCodeField.apply" />
          </Button>
        </div>
      </div>

      {validationError ? <div className={css.error}>{validationError}</div> : null}

      <div className={css.helpText}>
        <FormattedMessage id="CouponCodeField.helpText" />
      </div>
    </div>
  );
};

CouponCodeField.propTypes = {
  className: string,
  rootClassName: string,
  name: string,
  onCouponApplied: func.isRequired,
  onCouponRemoved: func.isRequired,
  appliedCoupon: object,
  listingId: string.isRequired,
  orderData: object,
  disabled: bool,
  sessionTypeId: string,
};

export default CouponCodeField;

